import {
  BaseTypedValueType,
  ArrayTypedValue,
  TypedValue,
} from 'notebook/tables/typed-values/TypedValueConstants';

/**
 * A 2D range in which every cell holds the same value, e.g. a range over a table that does not exist.
 */
interface UniformRange {
  rows: number;
  columns: number;
  element: TypedValue;
}

/**
 * Builds a rows x columns range in which every row is the same array object, so construction cost is
 * proportional to rows + columns rather than rows * columns. Callers must treat the result as immutable.
 */
export function makeUniformRange(rows: number, columns: number, element: TypedValue): ArrayTypedValue {
  const row: ArrayTypedValue = {
    type: BaseTypedValueType.ARRAY,
    rawValue: new Array<TypedValue>(columns).fill(element),
  };
  return {
    type: BaseTypedValueType.ARRAY,
    rawValue: new Array<TypedValue>(rows).fill(row),
  };
}

/**
 * Returns the shape and shared element of a range whose rows are all the same array object and whose
 * cells are all the same value, as built by makeUniformRange, or undefined for any other value. The
 * check compares references only, so it costs rows + columns comparisons rather than rows * columns
 * coercions.
 */
export function getUniformRange(value: unknown): UniformRange | undefined {
  if (!isArrayTypedValue(value) || value.rawValue.length === 0) {
    return undefined;
  }
  const firstRow = value.rawValue[0];
  if (!isArrayTypedValue(firstRow) || firstRow.rawValue.length === 0) {
    return undefined;
  }
  if (!value.rawValue.every((row) => row === firstRow)) {
    return undefined;
  }
  const element = firstRow.rawValue[0];
  if (!firstRow.rawValue.every((cell) => cell === element)) {
    return undefined;
  }
  return {rows: value.rawValue.length, columns: firstRow.rawValue.length, element};
}

function isArrayTypedValue(value: unknown): value is ArrayTypedValue {
  return (
    value != null &&
    typeof value === 'object' &&
    (value as TypedValue).type === BaseTypedValueType.ARRAY &&
    Array.isArray((value as ArrayTypedValue).rawValue)
  );
}
